import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Input,
  Label,
  Button,
  Form,
  FormFeedback,
} from "reactstrap";
import Loader from "../../components/Common/Loader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//redux
import { useSelector, useDispatch } from "react-redux";

import { withRouter, Link } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import { loginUser, resetLoginFlag } from "../../store/actions";
import AuthPageLayout from "../../layouts/VerticalLayouts/AuthPageLayout";
import { translateApiError } from "../../helpers/translateApiError";

const Login = (props) => {
  const { t } = useTranslation();
  document.title = t("common.pageTitle", { page: t("auth.login.pageTitle") });

  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => ({
    user: state.Account.user,
    loading: state.Login.loading,
  }));

  const [userLogin, setUserLogin] = useState([]);

  useEffect(() => {
    if (user && user) {
      setUserLogin({
        email: user.user.email,
        password: user.user.confirm_password,
        rememberMe: user.user.rememberMe,
      });
    }
  }, [user]);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      isSuperAdmin: false,
      email: userLogin.email || "",
      password: userLogin.password || "",
      rememberMe: userLogin.rememberMe || false,
    },
    validationSchema: Yup.object({
      isSuperAdmin: Yup.boolean(),
      email: Yup.string()
        .required(t("auth.validation.enterEmail"))
        .when("isSuperAdmin", {
          is: false,
          then: Yup.string().matches(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            t("auth.validation.invalidEmail")
          ),
        }),
      password: Yup.string().required(t("auth.validation.enterPassword")),
      rememberMe: Yup.boolean(),
    }),
    onSubmit: (values) => {
      dispatch(loginUser(values, props.history));
    },
  });

  const { error, errorCount } = useSelector((state) => ({
    error: state.Login.error,
    errorCount: state.Login.errorCount,
  }));

  // Show error toast when error changes
  useEffect(() => {
    if (error && errorCount > 0) {
      // Extract error message from various possible error formats
      let errorMessage = t("auth.login.error");

      if (typeof error === "string") {
        errorMessage = translateApiError(error);
      } else if (error.message) {
        errorMessage = translateApiError(error);
      } else if (error.data) {
        if (error.data.message) {
          errorMessage = translateApiError(error.data);
        } else if (typeof error.data === "string") {
          errorMessage = translateApiError(error.data);
        }
      }

      // Dismiss any existing toasts and show new one
      toast.dismiss();
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [errorCount, error]);

  useEffect(() => {
    validation.setFieldValue(
      "isSuperAdmin",
      props.history.location.pathname === "/admin" ? true : false
    );
  }, [props.history]);

  // to-do refactor this
  const passwordPeek = (elementID) => {
    const pw = document.querySelector(`[data-id='password']`);
    if (pw.type === "password") {
      pw.type = "text";
    } else {
      pw.type = "password";
    }
  };

  return (
    <React.Fragment>
      <AuthPageLayout>
        <ToastContainer />
        <Card className="mt-4">
          <CardBody className="p-4">
            <div className="mb-4">
              <h3 className="text-primary fw-semibold mb-2 auth-form-heading">
                {t("auth.login.heading")}
              </h3>
              <p className="text-muted mb-0 auth-form-subheading">
                {t("auth.login.subheading")}
              </p>
            </div>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
              action="#"
            >
              <div className="mb-3">
                <Label
                  htmlFor="email"
                  className="form-label fw-semibold auth-form-label"
                >
                  {t("common.labels.email")}{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  name="email"
                  className="form-control auth-form-input"
                  placeholder={t("auth.fields.emailPlaceholder")}
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email || ""}
                  invalid={
                    validation.touched.email && validation.errors.email
                      ? true
                      : false
                  }
                />
                {validation.touched.email && validation.errors.email ? (
                  <div className="text-danger mt-1 auth-form-error">
                    {validation.errors.email}
                  </div>
                ) : null}
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-end mb-1">
                  <Label
                    className="form-label fw-semibold mb-0 auth-form-label"
                    htmlFor="password-input"
                  >
                    {t("auth.fields.password")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  {/* Hide forget password if in admin login page. */}
                  {props.history.location.pathname !== "/admin" && (
                    <Link
                      to="/forgot-password"
                      className="text-muted auth-form-forgot-password"
                    >
                      {t("auth.login.forgotPassword")}
                    </Link>
                  )}
                </div>
                <div className="position-relative auth-pass-inputgroup">
                  <Input
                    data-id="password"
                    id="password"
                    name="password"
                    value={validation.values.password || ""}
                    type="password"
                    className="form-control pe-5 auth-form-input"
                    placeholder={t("auth.login.passwordPlaceholder")}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password && validation.errors.password
                        ? true
                        : false
                    }
                  />
                  <button
                    className={
                      "btn btn-link position-absolute end-0 top-0 text-decoration-none text-dark " +
                      (validation.errors.password && "has-error")
                    }
                    type="button"
                    id="password-addon"
                    onClick={() => passwordPeek()}
                  >
                    <i className="ri-eye-fill align-middle"></i>
                  </button>
                </div>
                {validation.touched.password && validation.errors.password ? (
                  <div className="text-danger mt-1 auth-form-error">
                    {validation.errors.password}
                  </div>
                ) : null}
              </div>

              <div className="form-check mb-3">
                <Input
                  data-id="rememberMe"
                  id="rememberMe"
                  name="rememberMe"
                  className="form-check-input"
                  type="checkbox"
                  value={validation.values.rememberMe}
                  checked={validation.values.rememberMe}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
                <Label
                  className="form-check-label auth-form-checkbox-label"
                  htmlFor="rememberMe"
                >
                  {t("auth.login.rememberMe")}
                </Label>
              </div>

              <div className="mb-3">
                <Button
                  color="primary"
                  type="submit"
                  className="w-100 fw-semibold auth-form-button"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader size="sm"></Loader>
                  ) : (
                    t("auth.actions.signIn")
                  )}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </AuthPageLayout>
    </React.Fragment>
  );
};

export default withRouter(Login);
